<script setup lang="ts">
import { computed } from 'vue';
import { VehicleSymbol } from 'shared/vehicles/domain/vehicleSymbol';
import type {
  NormalizedPoint,
  VehicleShapeDefinition,
} from 'shared/map/domain/vehicleSymbols/vehicleShape';
import { arrowShape } from 'shared/map/domain/vehicleSymbols/arrowShape';
import { wheelLoaderFrontGrappleShape } from 'shared/map/domain/vehicleSymbols/wheelLoaderFrontGrappleShape';
import { wheelLoaderHighGrappleShape } from 'shared/map/domain/vehicleSymbols/wheelLoaderHighGrappleShape';
import { timberCraneShape } from 'shared/map/domain/vehicleSymbols/timberCraneShape';
import { coilForkliftShape } from 'shared/map/domain/vehicleSymbols/coilForkliftShape';
import { logStackerShape } from 'shared/map/domain/vehicleSymbols/logStackerShape';

const props = defineProps<{
  symbol: VehicleSymbol;
  color?: string;
  size?: number;
  anchorPoint?: NormalizedPoint;
  editableAnchorPoint?: boolean;
  pickupPoint?: NormalizedPoint | null;
  rotationOffset?: number;
  activePoint?: 'anchor' | 'pickup';
}>();

const emit = defineEmits<{
  (e: 'update:anchorPoint', value: NormalizedPoint): void;
  (e: 'update:pickupPoint', value: NormalizedPoint): void;
}>();

const vehicleColor = computed(() => props.color ?? '#91a7ff');
const previewSize = computed(() => props.size ?? 120);

function getShapeDefinition(symbol: VehicleSymbol): VehicleShapeDefinition {
  switch (symbol) {
    case VehicleSymbol.LogStacker:
      return logStackerShape;
    case VehicleSymbol.WheelLoaderFrontGrapple:
      return wheelLoaderFrontGrappleShape;
    case VehicleSymbol.WheelLoaderHighGrapple:
      return wheelLoaderHighGrappleShape;
    case VehicleSymbol.TimberCrane:
      return timberCraneShape;
    case VehicleSymbol.CoilForklift:
      return coilForkliftShape;
    case VehicleSymbol.Undefined:
    default:
      return arrowShape;
  }
}

const shapeDefinition = computed(() => getShapeDefinition(props.symbol));

// Convert polygon coordinates to SVG path
function polygonToPath(coords: Array<[number, number]>): string {
  if (coords.length === 0) return '';

  const pathParts = coords.map((coord, index) => {
    const [x, y] = coord;
    const command = index === 0 ? 'M' : 'L';
    return `${command} ${x} ${y}`;
  });

  return pathParts.join(' ') + ' Z';
}

const polygonPaths = computed(() => {
  const colorConfig = shapeDefinition.value.colorConfig || {};

  return Object.entries(shapeDefinition.value.polygons).map(([name, coords]) => {
    // Determine the fill color based on colorConfig
    let fillColor = vehicleColor.value; // Default to dynamic vehicle color

    const config = colorConfig[name];
    if (config) {
      if (config.type === 'fixed' && config.color) {
        fillColor = config.color;
      }
      // If type is 'dynamic', use the default vehicle color (already set)
    }

    return {
      name,
      path: polygonToPath(coords),
      fillColor
    };
  });
});

const allPoints = computed(() => {
  return Object.values(shapeDefinition.value.polygons).flat();
});

const viewBounds = computed(() => {
  const points = allPoints.value;
  if (points.length === 0) {
    return {
      minX: -10,
      minY: -10,
      width: 120,
      height: 170,
    };
  }

  const xValues = points.map((p) => p[0]);
  const yValues = points.map((p) => p[1]);

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const padding = 10;

  return {
    minX: minX - padding,
    minY: minY - padding,
    width: Math.max(maxX - minX + padding * 2, 1),
    height: Math.max(maxY - minY + padding * 2, 1),
  };
});

const viewBox = computed(() => {
  const bounds = viewBounds.value;
  return `${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`;
});

const previewHeight = computed(() => {
  const bounds = viewBounds.value;
  return previewSize.value * (bounds.height / bounds.width);
});

const anchorX = computed(() => props.anchorPoint?.x ?? 50);
const anchorY = computed(() => props.anchorPoint?.y ?? 0);
const pickupX = computed(() => props.pickupPoint?.x ?? 50);
const pickupY = computed(() => props.pickupPoint?.y ?? 0);

// Show the rotation offset as an arrow at the anchor point without moving the icon.
const rotationOffset = computed(() => props.rotationOffset ?? 0);

const arrowLength = computed(() => {
  const bounds = viewBounds.value;
  return Math.max(Math.min(bounds.width, bounds.height) * 0.28, 12);
});

const rotationArrowTransform = computed(
  () => `rotate(${rotationOffset.value} ${anchorX.value} ${anchorY.value})`,
);

function onSvgPointerDown(event: PointerEvent) {
  if (!props.editableAnchorPoint) return;

  const svg = event.currentTarget as SVGSVGElement;
  const rect = svg.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const bounds = viewBounds.value;
  const x = bounds.minX + ((event.clientX - rect.left) / rect.width) * bounds.width;
  const y = bounds.minY + ((event.clientY - rect.top) / rect.height) * bounds.height;

  if (props.activePoint === 'pickup') {
    emit('update:pickupPoint', { x, y });
    return;
  }

  emit('update:anchorPoint', { x, y });
}
</script>

<template>
  <div class="vehicle-symbol-preview">
    <svg
      :width="previewSize"
      :height="previewHeight"
      :viewBox="viewBox"
      class="vehicle-svg"
      :class="{ editable: editableAnchorPoint }"
      @pointerdown="onSvgPointerDown"
    >
      <g>
        <g v-for="polygon in polygonPaths" :key="polygon.name">
          <path :d="polygon.path" :fill="polygon.fillColor" stroke="#000000" stroke-width="2" opacity="0.9" />
        </g>
      </g>
      <g v-if="rotationOffset" class="rotation-offset-arrow" :transform="rotationArrowTransform">
        <line :x1="anchorX" :y1="anchorY" :x2="anchorX" :y2="anchorY - arrowLength" stroke="#ff9500"
          stroke-width="2" stroke-linecap="round" />
        <polygon
          :points="`${anchorX},${anchorY - arrowLength - 4} ${anchorX - 4},${anchorY - arrowLength + 4} ${anchorX + 4},${anchorY - arrowLength + 4}`"
          fill="#ff9500" />
      </g>
      <g v-if="pickupPoint" class="pickup-point-marker">
        <line :x1="pickupX - 4" :y1="pickupY" :x2="pickupX + 4" :y2="pickupY" stroke="#0a84ff" stroke-width="1.5" />
        <line :x1="pickupX" :y1="pickupY - 4" :x2="pickupX" :y2="pickupY + 4" stroke="#0a84ff" stroke-width="1.5" />
        <circle :cx="pickupX" :cy="pickupY" r="2.5" fill="#0a84ff" stroke="#ffffff" stroke-width="1" />
      </g>
      <g class="anchor-point-marker">
        <line :x1="anchorX - 4" :y1="anchorY" :x2="anchorX + 4" :y2="anchorY" stroke="#ff2d55" stroke-width="1.5" />
        <line :x1="anchorX" :y1="anchorY - 4" :x2="anchorX" :y2="anchorY + 4" stroke="#ff2d55" stroke-width="1.5" />
        <circle :cx="anchorX" :cy="anchorY" r="2.5" fill="#ff2d55" stroke="#ffffff" stroke-width="1" />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.vehicle-symbol-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.vehicle-svg {
  display: block;
}

.vehicle-svg.editable {
  cursor: crosshair;
}
</style>
